// Resolve an Indian 6-digit pincode to its city + state via the free India Post
// API (no key, CORS-enabled). This cuts the shipping form for physical bundles to
// effectively pincode + house/street — name, phone and email are already prefilled
// from the chat capture. Soft-fails to null so checkout never breaks on a lookup
// hiccup; the user can always type city/state by hand.

export interface PincodeLocation {
  city: string;
  state: string;
}

interface IndiaPostOffice {
  Name?: string;
  Block?: string;
  District?: string;
  State?: string;
}

interface IndiaPostResponse {
  Status?: string;
  PostOffice?: IndiaPostOffice[] | null;
}

const PINCODE_RE = /^\d{6}$/;

export const isCompleteIndianPincode = (pin: string): boolean => PINCODE_RE.test(pin.trim());

const pickCity = (o: IndiaPostOffice): string => (o.District || o.Block || o.Name || "").trim();

function firstPostOffice(body: IndiaPostResponse[]): IndiaPostOffice | null {
  const entry = body?.[0];
  if (entry?.Status !== "Success") return null;
  return entry.PostOffice?.[0] ?? null;
}

function parsePincodeResponse(body: IndiaPostResponse[]): PincodeLocation | null {
  const office = firstPostOffice(body);
  if (!office) return null;
  const city = pickCity(office);
  const state = (office.State || "").trim();
  return city && state ? { city, state } : null;
}

export async function resolveIndianPincode(
  pin: string,
  signal?: AbortSignal
): Promise<PincodeLocation | null> {
  const value = pin.trim();
  if (!isCompleteIndianPincode(value)) return null;
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${value}`, { signal });
    if (!res.ok) return null;
    return parsePincodeResponse((await res.json()) as IndiaPostResponse[]);
  } catch {
    return null;
  }
}
